// ===========================================
// Parent Events Page
// ===========================================
// Lists PUBLISHED upcoming events for the parent's school.
// Each card links through to the detail/RSVP page.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  ConfirmationNumber as TicketIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useParentEvents } from '../../hooks/useEventsParent';
import { useTenant } from '../../contexts/TenantContext';
import { EventListItem, EventType } from '../../api/events.api';
import { tierPriceSummary } from '../../components/events/EventTierPricing';
import {
  EVENT_TYPE_COLORS,
  EVENT_TYPE_LABELS,
  eventTypeChipTextColor,
} from '../../constants/eventTypes';

// ===========================================
// EVENT CARD
// ===========================================

interface EventCardProps {
  event: EventListItem;
  onClick: () => void;
}

function EventCard({ event, onClick }: EventCardProps) {
  const startDate = new Date(event.startDateTime);
  const endDate = new Date(event.endDateTime);
  const isSameDay =
    format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');

  const typeColor = EVENT_TYPE_COLORS[event.type] ?? '#4580E4';

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `4px solid ${typeColor}`,
        boxShadow: 'none',
      }}
    >
      <CardActionArea onClick={onClick} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {event.featuredImage && (
          <CardMedia
            component="img"
            height="140"
            image={event.featuredImage}
            alt={event.title}
            sx={{ objectFit: 'cover' }}
          />
        )}
        <CardContent sx={{ flex: 1 }}>
          {/* Type chip */}
          <Chip
            label={EVENT_TYPE_LABELS[event.type] ?? event.type}
            size="small"
            sx={{
              mb: 1,
              backgroundColor: typeColor,
              color: eventTypeChipTextColor(event.type),
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />

          {/* Title */}
          <Typography variant="h6" sx={{ mb: 0.5, lineHeight: 1.3, fontSize: '1rem' }}>
            {event.title}
          </Typography>

          {/* Date/time */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <EventIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              {format(startDate, 'EEE d MMM yyyy')}
              {' '}
              {format(startDate, 'h:mm a')}
              {isSameDay
                ? ` – ${format(endDate, 'h:mm a')}`
                : ` – ${format(endDate, 'EEE d MMM, h:mm a')}`}
            </Typography>
          </Stack>

          {/* Venue */}
          {event.venueName && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <LocationIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                {event.venueName}
              </Typography>
            </Stack>
          )}

          {/* Paid indicator */}
          {!event.isFree &&
            (() => {
              const price =
                tierPriceSummary(event.EventTicketTier) ??
                (event.ticketPriceCents != null
                  ? `$${(event.ticketPriceCents / 100).toFixed(2)} per ticket`
                  : null);
              return price ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <TicketIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                  <Typography variant="body2" color="primary.main" fontWeight={600}>
                    {price}
                  </Typography>
                </Stack>
              ) : null;
            })()}

          {event.isFree && (
            <Chip
              label="Free"
              size="small"
              sx={{ backgroundColor: '#96DAC9', color: '#080808', fontWeight: 600, mt: 0.5 }}
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ===========================================
// MAIN PAGE
// ===========================================

export default function ParentEventsPage() {
  const { schoolSlug } = useTenant();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<EventType | ''>('');

  const { data, isLoading, isError } = useParentEvents({ limit: 50 });

  const events: EventListItem[] = data?.data ?? [];

  // Client-side filter (list is small — no server-side search needed for parents)
  const filtered = events.filter((e) => {
    const matchesSearch =
      !searchText ||
      e.title.toLowerCase().includes(searchText.toLowerCase()) ||
      (e.venueName ?? '').toLowerCase().includes(searchText.toLowerCase());
    const matchesType = !typeFilter || e.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleEventClick = (id: string) => {
    navigate(schoolSlug ? `/${schoolSlug}/parent/events/${id}` : `events/${id}`);
  };

  return (
    <Box>
      <PageHeader
        title="Events"
        subtitle="Upcoming events at your school"
        breadcrumbs={[{ label: 'Dashboard', path: '..' }, { label: 'Events' }]}
      />

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search events…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="event-type-filter-label">Type</InputLabel>
          <Select
            labelId="event-type-filter-label"
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value as EventType | '')}
          >
            <MenuItem value="">All types</MenuItem>
            {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">Failed to load events. Please try again.</Alert>
      ) : events.length === 0 ? (
        <Alert severity="info" icon={<EventIcon />}>
          No upcoming events have been published yet. Check back soon.
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No events match your search.</Alert>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((event) => (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <EventCard event={event} onClick={() => handleEventClick(event.id)} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
